import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import {
  createTranslator,
  LOCALE_INTL_TAGS,
  LOCALE_TAGS,
  localePath,
  type Locale,
  type TranslateFn,
} from '@nk/shared';

interface LocaleContextValue {
  locale: Locale;
  /** BCP-47 tag, for `<html lang>` and `hreflang`. */
  tag: string;
  /** The formatting tag, with the numbering system pinned — see `LOCALE_INTL_TAGS`. */
  intlTag: string;
  t: TranslateFn;
  /** Builds a path in the active locale. */
  path: (...segments: string[]) => string;
  alternates: Partial<Record<Locale, string>> | null;
  setAlternates: (alternates: Partial<Record<Locale, string>> | null) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  const [alternates, setAlternates] = useState<Partial<Record<Locale, string>> | null>(null);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      tag: LOCALE_TAGS[locale],
      intlTag: LOCALE_INTL_TAGS[locale],
      t: createTranslator(locale),
      path: (...segments) => localePath(locale, ...segments),
      alternates,
      setAlternates,
    }),
    [locale, alternates],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used inside <LocaleProvider>');
  return ctx;
}
